import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoachLayout } from '../components/CoachLayout';
import { reportsAPI, lessonsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Download, Users as UsersIcon, Calendar, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadPDF } from '../lib/pdfDownload';
import { useAuth } from '../context/AuthContext';

const ITEMS_PER_PAGE = 10;

export default function ReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [athleteReports, setAthleteReports] = useState([]);
  const [dateReports, setDateReports] = useState([]);
  const [periodType, setPeriodType] = useState('all');
  const [monthKey, setMonthKey] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPageAthletes, setCurrentPageAthletes] = useState(1);
  const [currentPageDates, setCurrentPageDates] = useState(1);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const hasLoadedRef = useRef(false);


  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('it-IT', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  useEffect(() => {
    fetchReports();
  }, [periodType, monthKey, customStart, customEnd]);

  const fetchReports = async () => {
    try {
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      let params = {};
      if (periodType === 'monthly' && monthKey) {
        const [year, month] = monthKey.split('-').map(Number);
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDateObj = new Date(year, month, 0);
        const endDate = `${endDateObj.getFullYear()}-${String(endDateObj.getMonth() + 1).padStart(2, '0')}-${String(endDateObj.getDate()).padStart(2, '0')}`;
        params = { start_date: startDate, end_date: endDate };
      } else if (periodType === 'custom' && customStart && customEnd) {
        params = { start_date: customStart, end_date: customEnd };
      }
      const [athleteRes, dateRes] = await Promise.all([
        reportsAPI.byAthlete(params),
        reportsAPI.byDate(params),
      ]);
      // Ordina atleti in ordine alfabetico
      const sortedAthletes = athleteRes.data.sort((a, b) => 
        a.athlete_name.localeCompare(b.athlete_name, 'it', { sensitivity: 'base' })
      );
      // Ordina date dalla più recente alla meno recente
      const sortedDates = dateRes.data.sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.start_time}`);
        const dateB = new Date(`${b.date}T${b.start_time}`);
        return dateB - dateA;
      });
      setAthleteReports(sortedAthletes);
      setDateReports(sortedDates);
    } catch (error) {
      toast.error('Errore nel caricamento dei report');
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  };

  const filterLesson = (lesson, filter) => {
    if (filter === 'unpaid') return lesson.coach_payment_status !== 'paid' || lesson.venue_payment_status !== 'paid';
    if (filter === 'paid') return lesson.coach_payment_status === 'paid' || lesson.venue_payment_status === 'paid';
    return true;
  };

  const filteredAthleteReports = useMemo(() => {
    if (paymentFilter === 'unpaid') return athleteReports.filter(a => (a.lessons || []).some(l => l.coach_payment_status !== 'paid' || l.venue_payment_status !== 'paid'));
    if (paymentFilter === 'paid') return athleteReports.filter(a => (a.lessons || []).some(l => l.coach_payment_status === 'paid' || l.venue_payment_status === 'paid'));
    return athleteReports;
  }, [athleteReports, paymentFilter]);

  const filteredDateReports = useMemo(() => {
    if (paymentFilter === 'unpaid') return dateReports.filter(l => (l.unpaid_amount || 0) > 0);
    if (paymentFilter === 'paid') return dateReports.filter(l => (l.unpaid_amount || 0) === 0 && (l.paid_amount || 0) > 0);
    return dateReports;
  }, [dateReports, paymentFilter]);

  // Paginazione atleti
  const totalPagesAthletes = Math.ceil(filteredAthleteReports.length / ITEMS_PER_PAGE);
  const startIndexAthletes = (currentPageAthletes - 1) * ITEMS_PER_PAGE;
  const paginatedAthletes = filteredAthleteReports.slice(startIndexAthletes, startIndexAthletes + ITEMS_PER_PAGE);

  // Paginazione date
  const totalPagesDates = Math.ceil(filteredDateReports.length / ITEMS_PER_PAGE);
  const startIndexDates = (currentPageDates - 1) * ITEMS_PER_PAGE;
  const paginatedDates = filteredDateReports.slice(startIndexDates, startIndexDates + ITEMS_PER_PAGE);

  const totals = athleteReports.reduce(
    (acc, athlete) => {
      const lessons = athlete.lessons || [];
      acc.coachTotal += lessons.reduce((sum, lesson) => sum + (lesson.coach_amount || 0), 0);
      acc.venueTotal += lessons.reduce((sum, lesson) => sum + (lesson.venue_amount || 0), 0);
      acc.coachUnpaid += lessons.reduce(
        (sum, lesson) => sum + (lesson.coach_payment_status === 'paid' ? 0 : (lesson.coach_amount || 0)),
        0
      );
      acc.venueUnpaid += lessons.reduce(
        (sum, lesson) => sum + (lesson.venue_payment_status === 'paid' ? 0 : (lesson.venue_amount || 0)),
        0
      );
      return acc;
    },
    { coachTotal: 0, coachUnpaid: 0, venueTotal: 0, venueUnpaid: 0 }
  );

  const markAsPaid = async (lessonId, athleteId, paymentType) => {
    try {
      await lessonsAPI.markPayment({ lesson_id: lessonId, athlete_id: athleteId, payment_type: paymentType });
      toast.success(`Pagamento ${paymentType === 'coach' ? 'allenatore' : 'struttura'} marcato come pagato!`);
      fetchReports();
    } catch (error) {
      toast.error('Errore nel marcare il pagamento');
    }
  };

  const unmarkAsPaid = async (lessonId, athleteId, paymentType) => {
    try {
      await lessonsAPI.unmarkPayment({ lesson_id: lessonId, athlete_id: athleteId, payment_type: paymentType });
      toast.success(`Pagamento ${paymentType === 'coach' ? 'allenatore' : 'struttura'} annullato!`);
      fetchReports();
    } catch (error) {
      toast.error('Errore nell\'annullare il pagamento');
    }
  };

  const exportAthleteReportPDF = async (athlete) => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.text("Trainers' Manager - Report Atleta", 14, 20);

      doc.setFontSize(12);
      doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);
      doc.text(`Atleta: ${athlete.athlete_name}`, 14, 42);
      doc.text(`Totale Lezioni: ${athlete.total_lessons}`, 14, 49);
      doc.text(`Compenso Allenatore: € ${athlete.total_coach_fee.toFixed(2)}`, 14, 56);
      doc.text(`Costo Struttura: € ${athlete.total_venue_cost.toFixed(2)}`, 14, 63);
      doc.text(`Totale: € ${athlete.total_amount.toFixed(2)}`, 14, 70);

      const tableData = athlete.lessons.map((lesson) => [
        lesson.date,
        lesson.start_time,
        `${lesson.duration_minutes} min`,
        lesson.venue_name,
        lesson.num_athletes,
        `€ ${(lesson.total_amount || 0).toFixed(2)}`,
      ]);

      autoTable(doc, {
        head: [['Data', 'Orario', 'Durata', 'Struttura', 'N. Atleti', 'Costo']],
        body: tableData,
        startY: 82,
        theme: 'grid',
        headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
      });

      const fileName = `report-${athlete.athlete_name.replace(/\s+/g, '-')}.pdf`;
      await downloadPDF(doc, fileName, {
        title: 'Report atleta',
        text: 'Report atleta pronto',
      });
      toast.success('Report pronto!');
    } catch (error) {
      console.error('Errore nell\'esportazione del report:', error);
      toast.error('Errore nell\'esportazione del report');
    }
  };

  const exportAllReportsPDF = async () => {
    try {
      let periodLabel = 'Tutto';
      if (periodType === 'monthly') {
        const [year, month] = monthKey.split('-').map(Number);
        const date = new Date(year, (month || 1) - 1, 1);
        periodLabel = date.toLocaleString('it-IT', { month: 'long', year: 'numeric' });
      } else if (periodType === 'custom' && customStart && customEnd) {
        periodLabel = `${customStart} \u2192 ${customEnd}`;
      }
      const doc = new jsPDF();

      doc.setFontSize(20);
      doc.text("Trainers' Manager - Report", 14, 20);

      doc.setFontSize(12);
      doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);
      doc.text(`Periodo: ${periodLabel}`, 14, 36);
      doc.text('Report per Atleta', 14, 43);

      const tableData = athleteReports.map((athlete) => ([
        athlete.athlete_name,
        athlete.total_lessons,
        `€ ${athlete.total_coach_fee.toFixed(2)}`,
        `€ ${athlete.total_venue_cost.toFixed(2)}`,
        `€ ${athlete.total_amount.toFixed(2)}`,
      ]));

      autoTable(doc, {
        head: [['Atleti', 'Lezioni', 'Compensi', 'Strutture', 'Totale']],
        body: tableData,
        startY: 53,
        theme: 'grid',
        headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 30, halign: 'right' },
          3: { cellWidth: 30, halign: 'right' },
          4: { cellWidth: 30, halign: 'right' },
        },
      });

      if (dateReports.length > 0) {
        doc.addPage();
        doc.setFontSize(12);
        doc.text('Report per Data', 14, 20);
        doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);

        const tableData = dateReports.map((lesson) => [
          lesson.date,
          lesson.start_time,
          lesson.venue_name,
          (lesson.athlete_names && lesson.athlete_names.length > 0)
            ? [...lesson.athlete_names].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' })).join(', ')
            : String(lesson.num_athletes || 0),
          `€ ${(lesson.coach_fee || 0).toFixed(2)}`,
          `€ ${(lesson.venue_cost || 0).toFixed(2)}`,
          `€ ${(lesson.total_cost || 0).toFixed(2)}`,
        ]);

        autoTable(doc, {
          head: [['Date', 'Orari', 'Strutture', 'Atleti', 'Compensi', 'Affitti', 'Totali']],
          body: tableData,
          startY: 38,
          theme: 'grid',
          headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
          columnStyles: {
            4: { halign: 'right' },
            5: { halign: 'right' },
            6: { halign: 'right' },
          },
        });
      }

      await downloadPDF(doc, 'report.pdf', {
        title: 'Report',
        text: 'Report pronto',
      });
      toast.success('Report pronto!');
    } catch (error) {
      console.error('Errore nell\'esportazione del report completo:', error);
      toast.error('Errore nell\'esportazione del report');
    }
  };

  if (loading) {
    return (
      <CoachLayout showFab={false}>
        <div className="flex items-center justify-center h-96">
          <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout showFab={false}>
      <div data-testid="reports-page" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2">Report</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Visualizza ed esporta i tuoi report</p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            <Select
              value={periodType}
              onValueChange={(value) => {
                setPeriodType(value);
                setCurrentPageAthletes(1);
                setCurrentPageDates(1);
                if (value === 'monthly') {
                  const now = new Date();
                  setMonthKey(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
                }
                if (value !== 'custom') {
                  setCustomStart('');
                  setCustomEnd('');
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px] bg-zinc-900 border-zinc-800 rounded-xl h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Tutto</SelectItem>
                <SelectItem value="monthly">Mensile</SelectItem>
                <SelectItem value="custom">Personalizzato</SelectItem>
              </SelectContent>
            </Select>

            {periodType === 'all' && (
              <Select value="all" onValueChange={() => {}}>
                <SelectTrigger className="w-full sm:w-[180px] bg-zinc-900 border-zinc-800 rounded-xl h-10">
                  <SelectValue placeholder="Tutto" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">Tutto</SelectItem>
                </SelectContent>
              </Select>
            )}

            {periodType === 'monthly' && (
              <Select value={monthKey} onValueChange={setMonthKey}>
                <SelectTrigger className="w-full sm:w-[220px] bg-zinc-900 border-zinc-800 rounded-xl h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {monthOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {periodType === 'custom' && (
              <div className="flex flex-row gap-2 w-full sm:w-auto">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="h-10 rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-sm text-white"
                />
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="h-10 rounded-xl border border-zinc-800 bg-zinc-900 px-3 text-sm text-white"
                />
              </div>
            )}

            <Button
              data-testid="export-all-btn"
              onClick={exportAllReportsPDF}
              className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl shadow-[0_0_15px_rgba(204,255,0,0.3)] h-10 text-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Crea Report
            </Button>
          </div>
        </div>

        {/* Filtro pagamenti */}
        <div className="flex w-full gap-2 mb-4">
          {[
            { value: 'all', label: 'Tutti' },
            { value: 'unpaid', label: 'Da pagare' },
            { value: 'paid', label: 'Pagati' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setPaymentFilter(opt.value);
                setCurrentPageAthletes(1);
                setCurrentPageDates(1);
              }}
              className={`flex-1 py-2 rounded-xl text-xs font-bold uppercase tracking-wide transition-colors ${
                paymentFilter === opt.value
                  ? 'bg-[#ccff00] text-black'
                  : 'bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-600'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div className="glass-card p-3 rounded-xl border border-white/10">
            <div className="text-xs uppercase tracking-wide text-zinc-400 mb-0.5">Totale Compensi</div>
            <div className="text-white font-bold text-lg">€ {totals.coachTotal.toFixed(2)}</div>
            <div className="text-red-400 text-sm">Da ricevere: € {totals.coachUnpaid.toFixed(2)}</div>
          </div>
          <div className="glass-card p-3 rounded-xl border border-white/10">
            <div className="text-xs uppercase tracking-wide text-zinc-400 mb-0.5">Totale Affitti</div>
            <div className="text-white font-bold text-lg">€ {totals.venueTotal.toFixed(2)}</div>
            <div className="text-red-400 text-sm">Da pagare: € {totals.venueUnpaid.toFixed(2)}</div>
          </div>
        </div>

        <Tabs defaultValue="athletes" className="space-y-3 sm:space-y-4">
          <TabsList className="bg-zinc-900/50 border border-white/10 rounded-xl w-full sm:w-auto">
            <TabsTrigger
              data-testid="athletes-tab"
              value="athletes"
              className="data-[state=active]:bg-[#ccff00] data-[state=active]:text-black rounded-xl text-xs sm:text-sm flex-1 sm:flex-none"
            >
              <UsersIcon className="w-4 h-4 mr-1 sm:mr-2" />
              Per Atleta ({filteredAthleteReports.length})
            </TabsTrigger>
            <TabsTrigger
              data-testid="dates-tab"
              value="dates"
              className="data-[state=active]:bg-[#ccff00] data-[state=active]:text-black rounded-xl text-xs sm:text-sm flex-1 sm:flex-none"
            >
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              Per Data ({filteredDateReports.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="athletes" className="space-y-3">
            {athleteReports.length === 0 ? (
              <div className="glass-card p-12 rounded-xl border border-white/10 text-center">
                <p className="text-zinc-400 text-lg">Nessun report disponibile</p>
              </div>
            ) : (
              <>
                {paginatedAthletes.map((athlete) => (
                  <div
                    key={athlete.athlete_id}
                    data-testid="athlete-report-card"
                    className="glass-card p-3 rounded-xl border border-white/10 hover:border-[#ccff00]/30 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-1">{athlete.athlete_name}</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <div className="text-zinc-400">Lezioni</div>
                            <div className="text-white font-bold">{athlete.total_lessons}</div>
                          </div>
                          <div>
                            <div className="text-zinc-400">Compenso</div>
                            <div className="text-white font-mono font-bold">€ {athlete.total_coach_fee.toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-400">Struttura</div>
                            <div className="text-white font-mono font-bold">€ {athlete.total_venue_cost.toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-400">Totale</div>
                            <div className="text-[#ccff00] font-mono font-black text-lg">€ {athlete.total_amount.toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-400">Pagato</div>
                            <div className="text-green-500 font-mono font-bold">€ {(athlete.paid_amount || 0).toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-400">Da Pagare</div>
                            <div className="text-red-500 font-mono font-bold">€ {(athlete.unpaid_amount || 0).toFixed(2)}</div>
                          </div>
                        </div>
                      </div>

                      <Button
                        data-testid="export-athlete-btn"
                        onClick={() => exportAthleteReportPDF(athlete)}
                        variant="outline"
                        className="bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700 rounded-xl"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </Button>
                    </div>

                    {athlete.lessons.filter(l => filterLesson(l, paymentFilter)).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/10">
                        <div className="text-xs text-zinc-400 mb-1 uppercase tracking-wide">Dettaglio Lezioni</div>
                        <div className="space-y-1">
                          {athlete.lessons.filter(l => filterLesson(l, paymentFilter)).map((lesson, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm p-2 bg-zinc-900/30 rounded-xl">
                              <div className="flex-1">
                                <span className="text-zinc-300">
                                  {lesson.date} {lesson.start_time} - {lesson.venue_name}
                                </span>
                                <div className="flex flex-col space-y-1 mt-1">
                                  {(paymentFilter === 'all' || (paymentFilter === 'paid' && lesson.coach_payment_status === 'paid') || (paymentFilter === 'unpaid' && lesson.coach_payment_status !== 'paid')) && (
                                  <div className="flex items-center space-x-2 flex-wrap gap-1">
                                    <span className="text-zinc-400 text-xs">Allenatore:</span>
                                    <span className={`font-mono text-sm ${
                                      lesson.coach_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                                    }`}>€ {lesson.coach_amount?.toFixed(2) || '0.00'}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-xl ${
                                      lesson.coach_payment_status === 'paid' 
                                        ? 'bg-green-900/30 text-green-500' 
                                        : 'bg-red-900/30 text-red-500'
                                    }`}>
                                      {lesson.coach_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE'}
                                    </span>
                                    {lesson.coach_payment_status === 'paid' ? (
                                      <Button
                                        size="sm"
                                        onClick={() => unmarkAsPaid(lesson.lesson_id, athlete.athlete_id, 'coach')}
                                        className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg"
                                      >
                                        <CheckCircle className="w-3 h-3" />
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        onClick={() => markAsPaid(lesson.lesson_id, athlete.athlete_id, 'coach')}
                                        className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg"
                                      >
                                        <XCircle className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                  )}
                                  {(paymentFilter === 'all' || (paymentFilter === 'paid' && lesson.venue_payment_status === 'paid') || (paymentFilter === 'unpaid' && lesson.venue_payment_status !== 'paid')) && (
                                  <div className="flex items-center space-x-2 flex-wrap gap-1">
                                    <span className="text-zinc-400 text-xs">Struttura:</span>
                                    <span className={`font-mono text-sm ${
                                      lesson.venue_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                                    }`}>€ {lesson.venue_amount?.toFixed(2) || '0.00'}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-xl ${
                                      lesson.venue_payment_status === 'paid' 
                                        ? 'bg-green-900/30 text-green-500' 
                                        : 'bg-red-900/30 text-red-500'
                                    }`}>
                                      {lesson.venue_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE'}
                                    </span>
                                    {lesson.venue_payment_status === 'paid' ? (
                                      <Button
                                        size="sm"
                                        onClick={() => unmarkAsPaid(lesson.lesson_id, athlete.athlete_id, 'venue')}
                                        className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg"
                                      >
                                        <CheckCircle className="w-3 h-3" />
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        onClick={() => markAsPaid(lesson.lesson_id, athlete.athlete_id, 'venue')}
                                        className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg"
                                      >
                                        <XCircle className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Paginazione Atleti */}
                {totalPagesAthletes > 1 && (
                  <div className="flex items-center justify-center space-x-4 mt-8">
                    <Button
                      onClick={() => setCurrentPageAthletes(p => Math.max(1, p - 1))}
                      disabled={currentPageAthletes === 1}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Indietro
                    </Button>
                    <span className="text-zinc-400">
                      Pagina {currentPageAthletes} di {totalPagesAthletes}
                    </span>
                    <Button
                      onClick={() => setCurrentPageAthletes(p => Math.min(totalPagesAthletes, p + 1))}
                      disabled={currentPageAthletes === totalPagesAthletes}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl"
                    >
                      Avanti
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="dates" className="space-y-3">
            {dateReports.length === 0 ? (
              <div className="glass-card p-8 sm:p-12 rounded-xl border border-white/10 text-center">
                <p className="text-zinc-400 text-sm sm:text-lg">Nessun report disponibile</p>
              </div>
            ) : (
              <>
                <div className="glass-card rounded-xl border border-white/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                      <thead className="bg-[#ccff00] text-black">
                        <tr>
                          <th className="px-3 py-3 text-left font-bold text-xs whitespace-nowrap">Data</th>
                          <th className="px-3 py-3 text-left font-bold text-xs whitespace-nowrap">Orario</th>
                          <th className="px-3 py-3 text-left font-bold text-xs whitespace-nowrap">Struttura</th>
                          <th className="px-3 py-3 text-left font-bold text-xs whitespace-nowrap">Atleti</th>
                          <th className="px-3 py-3 text-right font-bold text-xs whitespace-nowrap min-w-[90px]">Compenso</th>
                          <th className="px-3 py-3 text-right font-bold text-xs whitespace-nowrap min-w-[90px]">Struttura</th>
                          <th className="px-3 py-3 text-right font-bold text-xs whitespace-nowrap min-w-[110px]">Totale</th>
                          <th className="px-3 py-3 text-right font-bold text-xs whitespace-nowrap">✓ Pagato</th>
                          <th className="px-3 py-3 text-right font-bold text-xs whitespace-nowrap">✗ Da pagare</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDates.map((lesson, idx) => (
                          <tr
                            key={idx}
                            data-testid="date-report-row"
                            className="border-b border-white/10 hover:bg-zinc-900/50 transition-colors cursor-pointer"
                            onClick={() => {
                              if (lesson.lesson_id) {
                                navigate(`/lessons/${lesson.lesson_id}`);
                              }
                            }}
                          >
                            <td className="px-3 py-3 text-white text-sm whitespace-nowrap">{lesson.date}</td>
                            <td className="px-3 py-3 text-white text-sm whitespace-nowrap">{lesson.start_time}</td>
                            <td className="px-3 py-3 text-zinc-300 text-sm whitespace-nowrap">{lesson.venue_name}</td>
                            <td className="px-3 py-3 text-white text-sm whitespace-nowrap">
                              {lesson.athlete_names && lesson.athlete_names.length > 0 ? (
                                <div className="flex flex-col gap-0.5">
                                  {[...lesson.athlete_names]
                                    .sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }))
                                    .map((name, index) => (
                                    <span key={`${lesson.lesson_id || idx}-athlete-${index}`}>{name}</span>
                                  ))}
                                </div>
                              ) : (
                                lesson.num_athletes
                              )}
                            </td>
                            <td className="px-3 py-3 text-right text-white font-mono text-sm whitespace-nowrap min-w-[90px]">€{(lesson.coach_fee || 0).toFixed(2)}</td>
                            <td className="px-3 py-3 text-right text-white font-mono text-sm whitespace-nowrap min-w-[90px]">€{(lesson.venue_cost || 0).toFixed(2)}</td>
                            <td className="px-3 py-3 text-right text-white font-mono font-bold text-sm min-w-[110px]">
                              <span className="inline-flex whitespace-nowrap">€{(lesson.total_cost || 0).toFixed(2)}</span>
                            </td>
                            <td className="px-3 py-3 text-right text-green-500 font-mono text-sm whitespace-nowrap">€{(lesson.paid_amount || 0).toFixed(2)}</td>
                            <td className="px-3 py-3 text-right text-red-500 font-mono text-sm whitespace-nowrap">€{(lesson.unpaid_amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Paginazione Date */}
                {totalPagesDates > 1 && (
                  <div className="flex items-center justify-center space-x-2 sm:space-x-4 mt-6 sm:mt-8">
                    <Button
                      onClick={() => setCurrentPageDates(p => Math.max(1, p - 1))}
                      disabled={currentPageDates === 1}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                    >
                      <ChevronLeft className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Indietro</span>
                    </Button>
                    <span className="text-zinc-400 text-xs sm:text-sm">
                      {currentPageDates} / {totalPagesDates}
                    </span>
                    <Button
                      onClick={() => setCurrentPageDates(p => Math.min(totalPagesDates, p + 1))}
                      disabled={currentPageDates === totalPagesDates}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                    >
                      <span className="hidden sm:inline">Avanti</span>
                      <ChevronRight className="w-4 h-4 sm:ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </CoachLayout>
  );
}
